import React, { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Toaster, toast } from "sonner";
import { ArrowLeft, ArrowRight, Loader2, Check, ShieldCheck, AlertCircle, BookOpen, Layers3, Sparkles, Compass } from "lucide-react";
import SiteHeader from "../components/gridvest/SiteHeader";
import Footer from "../components/gridvest/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const UPDATES = [
  {
    icon: BookOpen,
    title: "Educational materials",
    text: "Plain-language explainers on infrastructure ownership, lease economics, and continuity models.",
  },
  {
    icon: Layers3,
    title: "Infrastructure participation concepts",
    text: "Conceptual frameworks for how owners and stakeholders may participate as the platform matures.",
  },
  {
    icon: Sparkles,
    title: "Infrastructure intelligence tools",
    text: "Benchmarks, renewal visibility, and portfolio-level analytics for long-duration agreements.",
  },
  {
    icon: Compass,
    title: "Future compliant participation models",
    text: "Structured pathways evaluated under applicable legal frameworks and official offering materials.",
  },
];

export default function FutureParticipationPage() {
  const [form, setForm] = useState({ name: "", email: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Please enter your name and email.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/future-updates`, {
        name: form.name.trim(),
        email: form.email.trim(),
      });
      setDone(true);
      toast.success("You're on the future updates list.");
    } catch (err) {
      const d = err?.response?.data?.detail;
      const msg = typeof d === "string" ? d : "Please review the fields.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#07111F] text-[#F8FAFC]" data-testid="future-page">
      <SiteHeader />

      <main className="relative">
        <div className="gv-grid-bg absolute inset-x-0 top-0 h-[460px]" aria-hidden />

        <section className="relative mx-auto max-w-3xl px-6 pb-16 pt-16 lg:px-8 lg:pt-24">
          <Link
            to="/"
            className="mb-10 inline-flex items-center gap-2 font-body text-base text-[#CBD5E1] transition-colors hover:text-[#F8FAFC]"
            data-testid="future-back-home"
          >
            <ArrowLeft className="h-5 w-5" />
            Back to home
          </Link>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          >
            <p className="mb-5 font-display text-sm font-extrabold uppercase tracking-[0.18em] text-[#38BDF8]">
              Roadmap
            </p>
            <h1 className="font-display text-[40px] font-extrabold leading-[1.05] tracking-[-0.03em] text-[#F8FAFC] sm:text-5xl lg:text-[60px]">
              Future Infrastructure
              <br className="hidden sm:block" /> Participation.
            </h1>
            <p className="mt-7 font-body text-lg leading-[1.75] text-[#E2E8F0] sm:text-xl">
              InfraYield is exploring future infrastructure participation
              opportunities as the platform evolves.
            </p>
          </motion.div>
        </section>

        {/* EXPLANATION */}
        <section className="relative mx-auto max-w-5xl px-6 pb-14 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6 }}
            className="space-y-7 font-body text-lg leading-[1.8] text-[#E2E8F0]"
          >
            <p>
              InfraYield's primary focus is{" "}
              <span className="font-semibold text-[#F8FAFC]">
                infrastructure visibility and operational continuity software
              </span>{" "}
              for long-duration infrastructure agreements.
            </p>

            <p>
              As the platform matures, InfraYield may evaluate future compliant
              participation opportunities related to infrastructure assets and
              infrastructure ownership workflows.
            </p>

            <div>
              <p className="font-display text-sm font-semibold uppercase tracking-[0.16em] text-[#38BDF8]">
                Future updates may include
              </p>
              <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2" data-testid="future-updates-list">
                {UPDATES.map((u, i) => (
                  <motion.article
                    key={u.title}
                    initial={{ opacity: 0, y: 14 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-60px" }}
                    transition={{ duration: 0.5, delay: i * 0.06 }}
                    className="group relative flex h-full flex-col overflow-hidden rounded-2xl border border-[#1E293B] bg-[#0F1B2D] p-6 transition-all duration-300 hover:-translate-y-0.5 hover:border-[#38BDF8]/40"
                    data-testid={`future-card-${i}`}
                  >
                    <div className="mb-5 flex items-center justify-between">
                      <div className="flex h-10 w-10 items-center justify-center rounded-lg border border-[#1E293B] bg-[#07111F] text-[#22C55E]">
                        <u.icon className="h-5 w-5" />
                      </div>
                      <span className="font-display text-xs font-extrabold text-[#334155]">
                        0{i + 1}
                      </span>
                    </div>
                    <h3 className="font-display text-lg font-semibold leading-snug text-[#F8FAFC] sm:text-xl">
                      {u.title}
                    </h3>
                    <p className="mt-3 font-body text-[15px] leading-[1.65] text-[#94A3B8]">
                      {u.text}
                    </p>
                  </motion.article>
                ))}
              </div>
              <p className="mt-6 font-body text-sm leading-relaxed text-[#94A3B8]">
                These reflect potential directions only — not a commitment, schedule,
                or offer.
              </p>
            </div>
          </motion.div>
        </section>

        {/* DISCLAIMER */}
        <section className="relative border-y border-[#1E293B] bg-[#0B1422]/40">
          <div className="mx-auto max-w-3xl px-6 py-14 lg:px-8" data-testid="future-disclaimer">
            <div className="flex items-start gap-4">
              <div className="mt-1 flex h-10 w-10 shrink-0 items-center justify-center rounded-md border border-[#1E293B] bg-[#07111F] text-[#38BDF8]">
                <AlertCircle className="h-5 w-5" />
              </div>
              <div className="space-y-4 font-body text-base leading-[1.8] text-[#CBD5E1] sm:text-lg">
                <p className="font-display text-sm font-semibold uppercase tracking-[0.16em] text-[#F8FAFC]">
                  Important Note
                </p>
                <p>
                  InfraYield is a platform focused on infrastructure ownership
                  intelligence, infrastructure organization, and related
                  infrastructure services. Nothing on this website constitutes an
                  offer to sell or a solicitation of an offer to buy any security.
                  Any investment opportunities, if offered in the future, will be
                  made only through compliant securities offerings and official
                  offering documents.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* SIGNUP */}
        <section className="relative mx-auto max-w-3xl px-6 py-20 lg:px-8 lg:py-24">
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6 }}
            className="rounded-3xl border border-[#1E293B] bg-[#0F1B2D] p-8 sm:p-10"
          >
            <div className="mb-7 flex items-start gap-4">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md border border-[#1E293B] bg-[#07111F] text-[#22C55E]">
                <ShieldCheck className="h-5 w-5" />
              </div>
              <div>
                <h2 className="font-display text-3xl font-bold tracking-[-0.03em] text-[#F8FAFC] sm:text-4xl">
                  Join Future Updates
                </h2>
                <p className="mt-3 font-body text-base leading-[1.75] text-[#E2E8F0] sm:text-lg">
                  Receive future updates regarding infrastructure participation
                  concepts, platform expansion, and future infrastructure
                  initiatives.
                </p>
              </div>
            </div>

            <form
              onSubmit={handleSubmit}
              className="space-y-5"
              data-testid="future-form"
            >
              <Field label="Name" required>
                <input
                  type="text"
                  required
                  value={form.name}
                  disabled={submitting || done}
                  onChange={(e) => update("name", e.target.value)}
                  placeholder="Jane Doe"
                  className={inputCls}
                  data-testid="future-name"
                />
              </Field>
              <Field label="Email Address" required>
                <input
                  type="email"
                  required
                  value={form.email}
                  disabled={submitting || done}
                  onChange={(e) => update("email", e.target.value)}
                  placeholder="jane@company.com"
                  className={inputCls}
                  data-testid="future-email"
                />
              </Field>

              <button
                type="submit"
                disabled={submitting || done}
                className="inline-flex w-full items-center justify-center gap-2.5 rounded-md bg-[#22C55E] px-7 py-4 font-body text-base font-semibold text-[#07111F] transition-colors hover:bg-[#22C55E]/90 disabled:opacity-70 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#22C55E]/40"
                data-testid="future-submit"
              >
                {submitting ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : done ? (
                  <><Check className="h-5 w-5" /> Subscribed</>
                ) : (
                  <>Join Future Updates <ArrowRight className="h-5 w-5" /></>
                )}
              </button>

              <p className="text-center font-body text-sm text-[#94A3B8]">
                Confidential. Unsubscribe anytime.
              </p>
            </form>
          </motion.div>
        </section>
      </main>

      <Footer />
      <Toaster
        theme="dark"
        position="top-right"
        toastOptions={{
          style: { background: "#0F1B2D", color: "#F8FAFC", border: "1px solid #1E293B", fontSize: "15px" },
        }}
      />
    </div>
  );
}

const inputCls =
  "w-full rounded-md border border-[#334155] bg-[#07111F] px-5 py-4 font-body text-base text-[#F8FAFC] placeholder:text-[#64748B] focus:border-[#38BDF8] focus:outline-none focus:ring-2 focus:ring-[#38BDF8]/40 disabled:opacity-60";

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="mb-2.5 inline-flex items-center gap-1.5 font-display text-sm font-semibold text-[#F8FAFC]">
        {label}
        {required && <span className="text-[#22C55E]" aria-label="required">*</span>}
      </span>
      {children}
    </label>
  );
}
